import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { buildApiUrl } from '@/config/runtimeConfig';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

export type AuditLogItem = components['schemas']['GetAuditLogsEndpoint_AuditLogResponse'];

export type AuditLogsPagedResponse = components['schemas']['GetAuditLogsEndpoint_AuditLogPagedResponse'];

export type AuditLogFilters = components['schemas']['AuditLogFilter'];

export interface UseAuditLogsReturn extends BaseQuery {
  auditLogs: AuditLogsPagedResponse | null;
}

export function useAuditLogs(page: number, pageSize: number, filters?: AuditLogFilters | null): UseAuditLogsReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getAuditLogs = async () => {
    if (!organizationId || organizationId === 0) {
      return {
        items: [],
        page,
        pageSize,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      } satisfies AuditLogsPagedResponse;
    }

    const queryParams = new URLSearchParams({
      Page: page.toString(),
      PageSize: pageSize.toString(),
    });

    const hasFilters =
      filters &&
      (filters.startDate != null ||
        filters.endDate != null ||
        filters.groupType != null ||
        (filters.groupNames != null && filters.groupNames.length > 0) ||
        (filters.userIds != null && filters.userIds.length > 0) ||
        (filters.performedBy != null && filters.performedBy.length > 0));

    const body = hasFilters
      ? {
          startDate: filters?.startDate ? dayjs(filters.startDate).utc().toISOString() : null,
          endDate: filters?.endDate ? dayjs(filters.endDate).utc().toISOString() : null,
          groupType: filters!.groupType ?? null,
          groupNames: filters!.groupNames ?? null,
          userIds: filters!.userIds ?? null,
          performedBy: filters!.performedBy ?? null,
        }
      : undefined;

    const token = await getAccessToken();
    const response = await fetch(buildApiUrl(`/api/organizations/${organizationId}/audit-logs?${queryParams.toString()}`), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch audit logs: ${response.status} ${response.statusText}`);
    }

    const data = (await response.json()) as AuditLogsPagedResponse;
    return data;
  };

  const query = useQuery({
    queryKey: ['auditLogs', organizationId, page, pageSize, filters],
    queryFn: getAuditLogs,
    enabled: !!organizationId && organizationId > 0,
    retry: false,
  });

  const { data, isLoading, isPending, error, refetch, isRefetching } = query;

  return {
    auditLogs: data ?? null,
    isLoading: isLoading || isPending,
    isRefetching,
    error: error as Error | null,
    refetch: () => {
      void refetch();
    },
  };
}
